import React, { useEffect, useRef, useState } from "react";
import { Box, Grid, MenuItem, InputLabel, Select, Button, CircularProgress } from "@mui/material";

import { ReactComponent as ArrowdownIcon } from "../../../../vendor/_new/icon/sites/arrow_down.svg";
import { ReactComponent as ArrowrightIcon } from "../../../../vendor/_new/icon/sites/arrow_right.svg";
import { SafeTextField, GreenSelectForm } from "../../../common/Greens";
import colors from "../../../../utils/colors";
import { useNotification } from "../../../../contexts/NotificationContext";
import useSite from "../../../../hooks/user/useSite";
import useSSLConfig from "../../../../hooks/user/useSSLConfig";
import useHome from "../../../../hooks/user/useHome";
import { validateAddDomainOrigin, validateAddDomainWebsite } from "./addDomainValidators";

const WebsiteInformation = ({ handleNext, setSiteUid }) => {
  const { createSite, selectSite } = useSite();
  const { getSSLConfig } = useSSLConfig();
  const { projectList, curProjectId, getAllProjectBaseList } = useHome();
  const { showError } = useNotification();

  const headerIsConcreteProject = Boolean(curProjectId) && curProjectId !== "all" && curProjectId !== "default";

  const [projectId, setProjectId] = useState(() => (headerIsConcreteProject ? curProjectId : ""));
  const [isSubmitting, setIsSubmitting] = useState(false);
  const siteIDRef = useRef(null);
  const siteIPRef = useRef(null);
  const [siteID, setSiteID] = useState("");
  const [siteIP, setSiteIP] = useState("");
  const [siteIDError, setSiteIDError] = useState(null);
  const [siteIPError, setSiteIPError] = useState(null);
  const [websiteTouched, setWebsiteTouched] = useState(false);
  const [originTouched, setOriginTouched] = useState(false);

  // Always show Select project once the list is available (All / Default / concrete)
  const showProjectSelect = projectList?.length > 0;
  // Honor the form value when visible (including empty "None").
  // While the list is still loading, fall back to a concrete header project id only.
  const effectiveProjectId = showProjectSelect
    ? projectId
    : headerIsConcreteProject
      ? curProjectId
      : "";

  useEffect(() => {
    // null = not loaded yet; [] = loaded empty — only fetch the unknown case
    if (projectList !== null) return;
    getAllProjectBaseList();
  }, [projectList, getAllProjectBaseList]);

  useEffect(() => {
    if (!headerIsConcreteProject) {
      setProjectId("");
      return;
    }
    setProjectId(curProjectId);
  }, [curProjectId, headerIsConcreteProject]);

  const applyWebsiteValidation = (value) => {
    const check = validateAddDomainWebsite(value);
    setSiteIDError(check.valid ? null : check.message);
    return check.valid;
  };

  const applyOriginValidation = (value) => {
    const check = validateAddDomainOrigin(value);
    setSiteIPError(check.valid ? null : check.message);
    return check.valid;
  };

  const websiteValid = validateAddDomainWebsite(siteID).valid;
  const originValid = validateAddDomainOrigin(siteIP).valid;
  const canContinue = websiteValid && originValid && !isSubmitting;

  const submitSiteInfo = async () => {
    setWebsiteTouched(true);
    setOriginTouched(true);
    const hostnameOk = applyWebsiteValidation(siteID);
    const originOk = applyOriginValidation(siteIP);
    if (!hostnameOk) {
      siteIDRef.current?.focus();
      return;
    }
    if (!originOk) {
      siteIPRef.current?.focus();
      return;
    }
    setIsSubmitting(true);
    const result = await createSite(String(siteID).trim(), String(siteIP).trim(), effectiveProjectId);
    if (result.status !== "success") {
      showError(result.msg);
      setIsSubmitting(false);
      return;
    }

    const _cursite = await selectSite(String(siteID).trim());
    if (!_cursite) {
      showError(`Site ${siteID} not found`);
      setIsSubmitting(false);
      return;
    }
    setSiteUid(_cursite.id);

    const result2 = await getSSLConfig(_cursite.id, true);
    if (result2.status !== "success") {
      showError(result.data);
      setIsSubmitting(false);
    }
    handleNext();
  };

  const handleChange = (event) => {
    setProjectId(event.target.value);
  };
  return (
    <>
      <Box sx={{ width: "100%", display: "flex", flexDirection: "column", flex: 1 }}>
        <Box sx={{ flexGrow: 0 }}>
          <Grid container spacing={6}>
            <Grid item xs={12} md={4}>
              <SafeTextField
                fullWidth
                sanitize={false}
                label="Add website"
                value={siteID}
                onChange={(e) => {
                  const next = e.target.value;
                  setSiteID(next);
                  if (websiteTouched) applyWebsiteValidation(next);
                }}
                onBlur={(e) => {
                  setWebsiteTouched(true);
                  applyWebsiteValidation(e.target.value);
                }}
                inputProps={{ maxLength: 256 }}
                error={Boolean(siteIDError)}
                helperText={siteIDError || undefined}
                inputRef={siteIDRef}
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <SafeTextField
                fullWidth
                sanitize={false}
                label="Origin (IP/FQDN)"
                value={siteIP}
                onChange={(e) => {
                  const next = e.target.value;
                  setSiteIP(next);
                  if (originTouched) applyOriginValidation(next);
                }}
                onBlur={(e) => {
                  setOriginTouched(true);
                  applyOriginValidation(e.target.value);
                }}
                inputProps={{ maxLength: 256 }}
                error={Boolean(siteIPError)}
                helperText={siteIPError || undefined}
                inputRef={siteIPRef}
              />
            </Grid>
            {showProjectSelect && (
              <Grid item xs={12} md={4}>
                <GreenSelectForm variant="outlined" fullWidth>
                  <InputLabel id="select-project-website-information" shrink>
                    Select project (optional)
                  </InputLabel>
                  <Select
                    labelId="select-project-website-information"
                    label="Select project (optional)"
                    notched
                    IconComponent={ArrowdownIcon}
                    value={projectId}
                    onChange={handleChange}
                    displayEmpty
                    renderValue={(selected) => {
                      if (!selected) return "None";
                      const match = projectList?.find((item) => item?.id === selected);
                      return match?.name || selected;
                    }}
                  >
                    <MenuItem
                      value=""
                      sx={{ height: 38, fontSize: 14, lineHeight: "21px", "&:hover": { color: colors.primary[300] } }}
                    >
                      None
                    </MenuItem>
                    {projectList?.map((item, index) => (
                      <MenuItem
                        key={`project_${index}`}
                        value={item?.id}
                        sx={{ height: 38, fontSize: 14, lineHeight: "21px", "&:hover": { color: colors.primary[300] } }}
                      >
                        {item?.name}
                      </MenuItem>
                    ))}
                  </Select>
                </GreenSelectForm>
              </Grid>
            )}
          </Grid>
        </Box>
        <Box sx={{ mt: "auto" }}>
          <Button
            endIcon={isSubmitting ? <CircularProgress size={16} sx={{ color: "#fff" }} /> : <ArrowrightIcon style={{ color: "#fff" }} />}
            onClick={submitSiteInfo}
            disabled={!canContinue}
            sx={{
              height: 36,
              backgroundColor: "#6371F7",
              border: "1px solid #6371F7",
              borderRadius: "8px",
              fontSize: 14,
              lineHeight: "21px",
              color: "#fff",
              fontWeight: "regular",
              textTransform: "none",
              px: 4,
              "&:hover": {
                backgroundColor: "#4959ee",
                borderColor: "#4959ee",
                color: "#fff",
              },
              "&.Mui-disabled": {
                backgroundColor: "#6371F7",
                borderColor: "#6371F7",
                color: "#fff",
                opacity: 0.7,
              },
            }}
          >
            {isSubmitting ? "Creating..." : "Continue"}
          </Button>
        </Box>
      </Box>
    </>
  );
};
export default WebsiteInformation;
